import { PreviousWorkSnapshot, AppointmentClockData } from "@/models/appointment.model";
import { Clock, MapPin, CheckCircle2, XCircle, Image, History, ChevronDown, ChevronLeft, ChevronRight, X } from "lucide-react";
import { useState } from "react";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";

interface PreviousWorkSectionProps {
  previousWork: PreviousWorkSnapshot[];
}

interface LightboxPhoto {
  image: string;
  title: string;
  description: string | null;
}

function formatTime(isoString: string) {
  return new Date(isoString).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDate(isoString: string) {
  return new Date(isoString).toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function getDuration(start: string, end: string) {
  const diff = new Date(end).getTime() - new Date(start).getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  return `${hours}h ${minutes}m`;
}

function ClockInfo({ clockIn, clockOut }: { clockIn?: AppointmentClockData; clockOut?: AppointmentClockData }) {
  if (!clockIn?.time) return null;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
      <div>
        <p className="text-xs text-muted-foreground uppercase">Started</p>
        <p className="font-medium">{formatTime(clockIn.time)}</p>
        {clockIn.lat != null && clockIn.lng != null && (
          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
            <MapPin className="w-3 h-3" />
            Location recorded
          </p>
        )}
      </div>
      {clockOut?.time && (
        <>
          <div>
            <p className="text-xs text-muted-foreground uppercase">Finished</p>
            <p className="font-medium">{formatTime(clockOut.time)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase">Duration</p>
            <p className="font-medium">{getDuration(clockIn.time, clockOut.time)}</p>
          </div>
        </>
      )}
    </div>
  );
}

function PhotoLightbox({
  photos,
  currentIndex,
  onClose,
  onNavigate,
}: {
  photos: LightboxPhoto[];
  currentIndex: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
}) {
  const photo = photos[currentIndex];
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < photos.length - 1;

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex flex-col" onClick={onClose}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 text-white" onClick={(e) => e.stopPropagation()}>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{photo.title}</p>
          {photo.description && (
            <p className="text-xs text-white/70 truncate">{photo.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2 ml-4">
          <span className="text-xs text-white/60">{currentIndex + 1} / {photos.length}</span>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/20 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Image */}
      <div className="flex-1 flex items-center justify-center p-4 relative" onClick={(e) => e.stopPropagation()}>
        {hasPrev && (
          <button
            onClick={() => onNavigate(currentIndex - 1)}
            className="absolute left-4 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors z-10"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}

        <img
          src={photo.image}
          alt={photo.title}
          className="max-w-full max-h-full rounded-lg object-contain"
        />

        {hasNext && (
          <button
            onClick={() => onNavigate(currentIndex + 1)}
            className="absolute right-4 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors z-10"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        )}
      </div>
    </div>
  );
}

function SingleVersionSection({ snapshot, versionLabel }: { snapshot: PreviousWorkSnapshot; versionLabel?: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const completedTasks = snapshot.tasks.filter((t) => t.done).length;
  const totalTasks = snapshot.tasks.length;

  // Group tasks by category
  const tasksByCategory = snapshot.tasks.reduce<Record<string, typeof snapshot.tasks>>((acc, task) => {
    const cat = task.task_category_name || "Uncategorized";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(task);
    return acc;
  }, {});

  // Build a unified photo gallery: clock-in, before photos, after photos, clock-out
  const allPhotos: LightboxPhoto[] = [];

  if (snapshot.clock_in?.photoUrl) {
    allPhotos.push({
      image: snapshot.clock_in.photoUrl,
      title: "Start Work Photo",
      description: snapshot.clock_in.time ? `Taken at ${formatTime(snapshot.clock_in.time)}` : null,
    });
  }

  const beforePhotos = snapshot.photos.filter((p) => !p.is_after);
  const afterPhotos = snapshot.photos.filter((p) => p.is_after);

  for (const photo of beforePhotos) {
    allPhotos.push({
      image: photo.image,
      title: `${photo.workflow_name || "Photo"} — Before`,
      description: photo.workflow_description || null,
    });
  }

  for (const photo of afterPhotos) {
    allPhotos.push({
      image: photo.image,
      title: `${photo.workflow_name || "Photo"} — After`,
      description: photo.workflow_description || null,
    });
  }

  if (snapshot.clock_out?.photoUrl) {
    allPhotos.push({
      image: snapshot.clock_out.photoUrl,
      title: "Complete Work Photo",
      description: snapshot.clock_out.time ? `Taken at ${formatTime(snapshot.clock_out.time)}` : null,
    });
  }

  // Track which index in allPhotos corresponds to each section for thumbnail rendering
  let clockInPhotoIndex: number | null = null;
  let clockOutPhotoIndex: number | null = null;
  const beforePhotoIndices: number[] = [];
  const afterPhotoIndices: number[] = [];

  let idx = 0;
  if (snapshot.clock_in?.photoUrl) {
    clockInPhotoIndex = idx;
    idx++;
  }
  for (let i = 0; i < beforePhotos.length; i++) {
    beforePhotoIndices.push(idx);
    idx++;
  }
  for (let i = 0; i < afterPhotos.length; i++) {
    afterPhotoIndices.push(idx);
    idx++;
  }
  if (snapshot.clock_out?.photoUrl) {
    clockOutPhotoIndex = idx;
  }

  const renderThumbnail = (photoIndex: number, alt: string) => (
    <button
      key={`thumb-${photoIndex}`}
      onClick={() => setLightboxIndex(photoIndex)}
      className="w-16 h-16 rounded-md overflow-hidden border border-border hover:ring-2 hover:ring-primary transition-all"
    >
      <img src={allPhotos[photoIndex].image} alt={alt} className="w-full h-full object-cover" />
    </button>
  );

  const headerLabel = versionLabel || "Previous Work (Before Go Back)";

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="border border-amber-200 bg-amber-50/50 rounded-lg">
        <CollapsibleTrigger className="w-full p-4 flex items-center justify-between cursor-pointer hover:bg-amber-100/50 transition-colors rounded-lg">
          <div className="flex items-center gap-2 text-sm font-medium text-amber-800">
            <History className="w-4 h-4" />
            {headerLabel}
            {(snapshot.started_at || snapshot.done_at) && (
              <span className="text-xs font-normal text-muted-foreground ml-1">
                {snapshot.started_at && `— Started ${formatDate(snapshot.started_at)}`}
                {snapshot.started_at && snapshot.done_at && ", "}
                {snapshot.done_at && `Finished ${formatDate(snapshot.done_at)}`}
              </span>
            )}
          </div>
          <ChevronDown className={`w-4 h-4 text-amber-800 transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-4 pb-4 space-y-4">
            {/* Clock times */}
            {snapshot.clock_in?.time && (
              <div className="bg-white/60 rounded-md p-3 space-y-2">
                <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                  <Clock className="w-3 h-3" />
                  Work Time
                </div>
                <ClockInfo clockIn={snapshot.clock_in} clockOut={snapshot.clock_out} />
              </div>
            )}

            {/* Tasks grouped by category */}
            {totalTasks > 0 && (
              <div className="bg-white/60 rounded-md p-3 space-y-3">
                <p className="text-xs font-medium text-muted-foreground">
                  Tasks ({completedTasks} of {totalTasks} completed)
                </p>
                {Object.entries(tasksByCategory).map(([category, tasks]) => (
                  <div key={category} className="space-y-1">
                    <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">{category}</p>
                    {tasks.map((task) => (
                      <div key={task.id} className="flex items-center gap-2 text-sm pl-1">
                        {task.done ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0" />
                        ) : (
                          <XCircle className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        )}
                        <span className={task.done ? "" : "text-muted-foreground"}>
                          {task.task_name || task.task_id}
                        </span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            {/* Photos */}
            {allPhotos.length > 0 && (
              <div className="bg-white/60 rounded-md p-3 space-y-3">
                <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                  <Image className="w-3 h-3" />
                  Photos ({allPhotos.length})
                </div>

                {/* Clock In Photo */}
                {clockInPhotoIndex !== null && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Start Work</p>
                    <div className="flex flex-wrap gap-2">
                      {renderThumbnail(clockInPhotoIndex, "Start Work")}
                    </div>
                  </div>
                )}

                {/* Before Photos */}
                {beforePhotoIndices.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Before</p>
                    <div className="flex flex-wrap gap-2">
                      {beforePhotoIndices.map((i) => renderThumbnail(i, "Before"))}
                    </div>
                  </div>
                )}

                {/* After Photos */}
                {afterPhotoIndices.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">After</p>
                    <div className="flex flex-wrap gap-2">
                      {afterPhotoIndices.map((i) => renderThumbnail(i, "After"))}
                    </div>
                  </div>
                )}

                {/* Clock Out Photo */}
                {clockOutPhotoIndex !== null && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Complete Work</p>
                    <div className="flex flex-wrap gap-2">
                      {renderThumbnail(clockOutPhotoIndex, "Complete Work")}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </CollapsibleContent>
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <PhotoLightbox
          photos={allPhotos}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
        />
      )}
    </Collapsible>
  );
}

export function PreviousWorkSection({ previousWork }: PreviousWorkSectionProps) {
  if (!previousWork || previousWork.length === 0) return null;

  // Single version — no numbering needed
  if (previousWork.length === 1) {
    return <SingleVersionSection snapshot={previousWork[0]} />;
  }

  // Multiple versions — show newest first with version labels
  return (
    <div className="space-y-3">
      {[...previousWork].reverse().map((snapshot, reverseIdx) => {
        const versionNum = previousWork.length - reverseIdx;
        return (
          <SingleVersionSection
            key={reverseIdx}
            snapshot={snapshot}
            versionLabel={`Go Back #${versionNum}`}
          />
        );
      })}
    </div>
  );
}
